import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Star, Heart, ArrowRight } from "lucide-react";

export default function HeroSection() {
  return (
    <section id="hero" className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 bg-amber-50 overflow-hidden" data-testid="hero-section">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-rose-100/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <Badge className="rounded-full px-4 py-1.5 bg-rose-100 text-rose-600 border-rose-200 font-semibold text-xs mb-6 hover:bg-rose-100">
              <Star className="w-3 h-3 mr-1 fill-rose-500 text-rose-500" />
              Loved by 5,000+ Indian Parents
            </Badge>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-indigo-950 leading-[1.1] tracking-tight mb-6" data-testid="hero-headline">
              Your Child is the Hero of Their Own Story
              <span className="text-rose-500"> — Literally.</span>
            </h1>

            <p className="text-base sm:text-lg text-indigo-950/60 leading-relaxed max-w-xl mb-8" data-testid="hero-subheadline">
              Upload a photo. Pick a story. We print a beautiful book where your child is the star. Delivered to your doorstep in 5-7 days.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                data-testid="hero-cta-button"
                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold text-base px-8 py-6 shadow-xl shadow-rose-500/25 hover:-translate-y-1 hover:shadow-2xl transition-all group"
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
              >
                Create My Child's Book
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                <span className="text-rose-200 ml-1 text-sm font-medium">Rs.499 only</span>
              </Button>
              <Button
                data-testid="hero-preview-button"
                variant="outline"
                className="rounded-full border-2 border-indigo-950/10 text-indigo-950 font-semibold text-base px-8 py-6 hover:bg-white hover:border-rose-200 hover:text-rose-500 transition-all"
                onClick={() => document.getElementById("story-preview")?.scrollIntoView({ behavior: "smooth" })}
              >
                See a Free Preview First
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 items-center" data-testid="trust-badges">
              <div className="flex items-center gap-2 text-sm text-indigo-950/50">
                <ShieldCheck className="w-4 h-4 text-green-500" />
                <span className="font-medium">100% Safe</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-indigo-950/50">
                <Heart className="w-4 h-4 text-rose-400 fill-rose-400" />
                <span className="font-medium">Loved by Kids</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-indigo-950/50">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                <span className="font-medium">4.9/5 Rating</span>
              </div>
            </div>
          </motion.div>

          {/* Right: Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-rose-900/10 border border-white/50">
              <img
                src="https://images.pexels.com/photos/7494478/pexels-photo-7494478.jpeg"
                alt="Child reading a personalised storybook"
                className="w-full h-[400px] sm:h-[500px] object-cover"
                data-testid="hero-image"
              />
              {/* Overlay card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md rounded-2xl p-4 border border-white/50 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl" role="img" aria-label="sparkles">&#10024;</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-indigo-950">"Mumma, that's ME in the book!"</p>
                    <p className="text-xs text-indigo-950/50">— Every child's reaction</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-300/60 rounded-full blur-xl pointer-events-none" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-rose-300/40 rounded-full blur-xl pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
